import * as React from 'react';
import styled from '@emotion/styled';
import * as Color from '../consts/variables';
import { replacePlaceholders, __ } from '../helpers/TranslationService';
import { Discount } from './Discount';
import { CalamityFundChargeText } from './CalamityFundText';
import { colors } from '../theme/colors';
import { AttentionIcon } from './Icons/AttentionIcon';

let style: Record<string, React.CSSProperties> = {
    wrapper: {
        background: Color.lightGray,
        padding: 15,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontSize: 13,
    },
    price: {
        fontWeight: 'bold',
        fontSize: 30,
    },
    hotelServiceChargesLabel: {
        margin: '15px 15px 10px 15px',
        fontWeight: 'bold',
    },
    hint: {
        margin: '15px 15px 10px 15px',
        color: Color.black,
        fontSize: 12,
    },
    discountArrow: {
        display: 'inline-flex',
        flex: 1,
        alignSelf: 'center',
    },
    priceRows: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    discountText: {
        color: Color.pink,
        fontWeight: 'bold',
        marginBottom: '7px',
    },
};

const StyledPriceOnRequest = styled.div`
    font-size: 14;
    font-weight: 600;
    color: ${(props) => props.theme.text.color.primary};
    border: 2px solid ${(props) => props.theme.alert.attention.border};
    borderwidth: 2px 0;
    background: ${(props) => props.theme.alert.attention.background};
    padding: 16px 24px 16px 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        fill: ${(props) => props.theme.alert.attention.icon};
        margin-right: 8px;
    }
`;

const TotalValue = styled.div`
    font-weight: 600;
    font-size: 20px;
    display: flex;
    align-items: center;
    margin-top: 16px;

    p {
        margin: 0 4px 0 0;
    }
`;

interface IProps {
    price: string | JSX.Element;
    discount: number;
    isEstimatedPrice?: boolean;
    withBackground?: boolean;
    isDualCurrency?: boolean;
    priceDefaultCurrency?: string;
    discountThreshold?: number;
    cabin?: string;
    discountType?: 'badge' | 'top';
    colorizePrice?: boolean;
    withFlight?: boolean;
    onRequest?: boolean;
    hasHotelServiceCharges?: boolean;
    market?: string;
    totalPrice?: string;
    showTotal?: boolean;
}

export function PerPersonPriceBig(props: IProps) {
    const {
        discount,
        price,
        isEstimatedPrice,
        isDualCurrency,
        priceDefaultCurrency,
        discountThreshold,
        cabin,
        discountType = 'badge',
        colorizePrice = false,
        withFlight = false,
        onRequest = false,
        hasHotelServiceCharges,
        market,
    } = props;

    const hasDiscount = discountThreshold ? discount >= discountThreshold : discount > 0;
    const showDualCurrencyHint = isDualCurrency && Boolean(priceDefaultCurrency);

    const wrapperStyle = {
        ...style.wrapper,
        ...(props.withBackground ? {} : { backgroundColor: Color.white }),
        color: hasDiscount && colorizePrice ? Color.pink : Color.black,
    };

    if (!price || onRequest) {
        return (
            <StyledPriceOnRequest>
                <AttentionIcon width={16} height={16} />
                <span>{__('on request', 'dreamlines')} </span>
            </StyledPriceOnRequest>
        );
    }

    if (isEstimatedPrice) {
        return (
            <StyledPriceOnRequest>
                <AttentionIcon width={16} height={16} />
                <span>{__('priceOnRequest', 'dreamlines')}</span>
            </StyledPriceOnRequest>
        );
    }

    return (
        <div style={wrapperStyle}>
            <div style={style.priceRows}>
                {hasDiscount && discountType === 'top' && (
                    <div style={style.discountText}>
                        {replacePlaceholders(__('upToXDiscount', 'dreamlines'), [`-${discount}%`])}
                    </div>
                )}
                {cabin && <div>{cabin}</div>}
                <div style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                    {hasDiscount && discountType === 'badge' && (
                        <div style={style.discountArrow}>
                            <Discount discount={discount} />
                        </div>
                    )}
                    {!isEstimatedPrice && (
                        <span style={{ marginRight: '5px' }}>{`${__('from', 'dreamlines')} `}</span>
                    )}
                    {isDualCurrency && `~`}
                    <span style={style.price}>{price}</span>
                    {isDualCurrency && `*`}
                    {/* Grows to center the price */}
                    {hasDiscount && discountType === 'badge' && (
                        <div style={{ display: 'flex', flex: 1 }} />
                    )}
                </div>
                <div>
                    <span>
                        {__('Price', 'dreamlines')}
                        {withFlight && ` ${__('with Flight', 'dreamlines')}`}
                    </span>
                </div>
                {hasHotelServiceCharges && (
                    <div style={style.hotelServiceChargesLabel}>
                        <span>{__('incl. hotel service charges', 'dreamlines')}</span>
                    </div>
                )}
                {market === 'dreamlines.nl' && <CalamityFundChargeText />}
                {showDualCurrencyHint && (
                    <div style={style.hint}>{`*${__(
                        'mobile.request.dualCurrencyHint',
                        'dreamlines',
                    )} ${priceDefaultCurrency}`}</div>
                )}

                {props.showTotal && props.totalPrice && (
                    <TotalValue>
                        <p>{__('cruiseData.total', 'dreamlines')}</p>
                        <span> {props.totalPrice} </span>
                    </TotalValue>
                )}
            </div>
        </div>
    );
}
